import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AwsWafCaptcha from "components/AwsWafCaptcha";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import IllustrationLayout from "layouts/authenticationLayout/index";
import { isWafConfigured } from "config/authApi";
import { requestPasswordReset } from "services/authRestService";
import { isValidEmail } from "utils/validation";
import lock from "assets/images/illustrations/lock.png";

function ResetPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wafToken, setWafToken] = useState(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const captchaRequired = isWafConfigured();
  const showCaptcha = isValidEmail(email);
  const requestFormSignature = email.trim().toLowerCase();
  const previousRequestSignatureRef = useRef("");

  const canSubmit =
    email.trim() && (!captchaRequired || wafToken) && !isSubmitting;

  useEffect(() => {
    if (!captchaRequired) {
      setWafToken(null);
    }
  }, [captchaRequired]);

  useEffect(() => {
    if (!showCaptcha) {
      previousRequestSignatureRef.current = "";
      setWafToken(null);
      return;
    }

    if (
      previousRequestSignatureRef.current &&
      previousRequestSignatureRef.current !== requestFormSignature
    ) {
      setWafToken(null);
      setCaptchaResetKey((current) => current + 1);
    }

    previousRequestSignatureRef.current = requestFormSignature;
  }, [showCaptcha, requestFormSignature]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (captchaRequired && !wafToken) {
      setError("Please complete the security check before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      await requestPasswordReset({ email: email.trim() });
      navigate("/sign-in", {
        replace: true,
        state: {
          message:
            "If an account exists for this email, reset instructions have been sent.",
        },
      });
    } catch (err) {
      setError(err?.message || "Unable to send reset instructions. Please try again.");
      setWafToken(null);
      setCaptchaResetKey((current) => current + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IllustrationLayout
      title="Forgot password"
      description="Enter your email and we will send reset instructions if an account exists"
      illustration={{
        image: lock,
        title: "Soft UI Design",
        description:
          "Just as it takes a company to sustain a product, it takes a community to sustain a protocol.",
      }}
    >
      <SoftBox component="form" role="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <SoftBox mb={2} p={2} bgColor="error" borderRadius="lg">
            <SoftTypography variant="body2" color="white">
              {error}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftBox mb={2}>
          <SoftInput
            type="email"
            placeholder="Email"
            size="large"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={isSubmitting}
            required
            autoComplete="email"
          />
        </SoftBox>

        <AwsWafCaptcha
          enabled={showCaptcha}
          resetKey={captchaResetKey}
          onToken={setWafToken}
          onError={() => setWafToken(null)}
        />

        <SoftBox mt={4} mb={1}>
          <SoftButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={!canSubmit}
          >
            {isSubmitting ? "Sending..." : "Send reset instructions"}
          </SoftButton>
        </SoftBox>

        <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Remember your password?{" "}
            <SoftTypography
              component={Link}
              to="/sign-in"
              variant="button"
              color="secondary"
              fontWeight="medium"
            >
              Sign in
            </SoftTypography>
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </IllustrationLayout>
  );
}

export default ResetPassword;
